import { z } from "zod";

export const HyperliquidUserRole = {
  USER: "user",
  MISSING: "missing",
  AGENT: "agent",
  WALLET: "wallet",
  SUBACCOUNT: "subaccount",
} as const;
export type HyperliquidUserRole = (typeof HyperliquidUserRole)[keyof typeof HyperliquidUserRole];

export const HyperliquidConnectionStatus = {
  WAITING_APPROVAL: "waitingApproval",
  WAITING_DEPOSIT: "waitingDeposit",
  WAITING_TRANSFER_TO_SPOT: "waitingTransferToSpot",
  ONLINE: "online",
} as const;
export type HyperliquidConnectionStatus =
  (typeof HyperliquidConnectionStatus)[keyof typeof HyperliquidConnectionStatus];

export const GetHyperliquidAgentSchema = z.object({
  accountId: z.number().int().positive(),
  agentAddress: z.string().min(1),
  agentName: z.string().min(1),
  status: z.enum(HyperliquidConnectionStatus),
});
export type GetHyperliquidAgentDto = z.infer<typeof GetHyperliquidAgentSchema>;

export const ApproveHyperliquidBodySchema = z.object({
  nonce: z.number().min(1),
  signature: z
    .string()
    .min(1)
    .regex(/^0x[0-9a-fA-F]{8,264}$/, {
      message: "Invalid EVM signature format.",
    }) as z.ZodType<`0x${string}`>,
});
export type ApproveHyperliquidBodyDto = z.infer<typeof ApproveHyperliquidBodySchema>;

export const HyperliquidStatusSchema = z.object({
  status: z.nativeEnum(HyperliquidConnectionStatus),
});
export type HyperliquidStatusDto = z.infer<typeof HyperliquidStatusSchema>;

// Referrer eligibility check schemas
export const HyperliquidReferrerEligibilityResultSchema = z.object({
  eligible: z.boolean(),
  reason: z.string().optional(),
  referrerCode: z.string().optional(),
  isWhitelisted: z.boolean().optional(),
});
export type HyperliquidReferrerEligibilityResult = z.infer<typeof HyperliquidReferrerEligibilityResultSchema>;

// Response from Hyperliquid referral API (for SDK)
export const HyperliquidReferralInfoSchema = z.object({
  referredBy: z
    .object({
      referrer: z.string(),
      code: z.string(),
    })
    .nullable()
    .optional(),
});
export type HyperliquidReferralInfo = z.infer<typeof HyperliquidReferralInfoSchema>;

/**
 * Response shape of HL info `userRateLimit` endpoint.
 *
 * HL enforces an address-based request limit:
 *   `remaining = nRequestsCap - nRequestsUsed + nRequestsSurplus`
 *
 * - `cumVlm` — cumulative traded volume in USD-ish terms. Native HL volume
 *    credits 1:1, HIP-3 volume credits at 10%.
 * - `nRequestsCap` — current cap (10,000 initial buffer + 1 per $1 of `cumVlm`).
 * - `nRequestsUsed` — requests consumed so far.
 * - `nRequestsSurplus` — extra quota purchased via `reserveRequestWeight`
 *    (0.0005 USDC per request from the Perps balance).
 *
 * @see https://nktkas.gitbook.io/hyperliquid/api-reference/info-methods/userratelimit
 */
export type UserRateLimitResponse = {
  cumVlm: string;
  nRequestsUsed: number;
  nRequestsCap: number;
  nRequestsSurplus: number;
};

/**
 * Response shape of HL exchange `reserveRequestWeight` action.
 *
 * Purchases extra address-based request quota for 0.0005 USDC per weight.
 * The cost is paid from the Perps balance. On success HL returns a
 * `{ status: "ok", response: { type: "default" } }` envelope.
 */
export type ReserveRequestWeightResponse = {
  status: string;
  response?: {
    type: string;
    data?: unknown;
  };
};
